import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { RefreshCw, Search, X, ChevronDown, ChevronUp, Users, BookOpen, UserCheck, Award } from 'lucide-react';

const PedagogiqueDashboard = () => {
  const [etudiants, setEtudiants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cours, setCours] = useState([]);
  const [professeurs, setProfesseurs] = useState([]);
  const [stats, setStats] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState(null);
  const [headerVisible, setHeaderVisible] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [filtreAnneeScolaire, setFiltreAnneeScolaire] = useState('2025/2026');
  const [filtreNiveau, setFiltreNiveau] = useState('tous');

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  useEffect(() => {
    fetchData();
    getUserInfo();
  }, []);

  const getUserInfo = () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserInfo({
          nom: payload.nom,
          filiere: payload.filiere,
          role: payload.role,
          estGeneral: payload.filiere === 'GENERAL'
        });
      }
    } catch (error) {
      setError("Erreur d'authentification");
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Token d'authentification manquant");

      const [etudiantsRes, coursRes, professeursRes, statsRes] = await Promise.all([
        fetch('    https://vmi1977988.contaboserver.net/api2/pedagogique/etudiants', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('    https://vmi1977988.contaboserver.net/api2/pedagogique/cours', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('    https://vmi1977988.contaboserver.net/api2/pedagogique/professeurs', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('    https://vmi1977988.contaboserver.net/api2/pedagogique/dashboard-stats', { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (!etudiantsRes.ok || !coursRes.ok || !professeursRes.ok || !statsRes.ok)
        throw new Error('Erreur lors du chargement des données');

      const [etudiantsData, coursData, professeursData, statsData] = await Promise.all([
        etudiantsRes.json(), coursRes.json(), professeursRes.json(), statsRes.json()
      ]);

      setEtudiants(etudiantsData);
      setCours(coursData);
      setProfesseurs(professeursData);
      setStats(statsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredEtudiants = () => {
    let filtered = [...etudiants];
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(e =>
        (e.nom && e.nom.toLowerCase().includes(s)) ||
        (e.prenom && e.prenom.toLowerCase().includes(s)) ||
        (e.filiere && e.filiere.toLowerCase().includes(s)) ||
        (e.cin && e.cin.toLowerCase().includes(s))
      );
    }
    if (filtreAnneeScolaire !== 'toutes') {
      filtered = filtered.filter(e => e.anneeScolaire === filtreAnneeScolaire);
    }
    if (filtreNiveau !== 'tous') filtered = filtered.filter(e => e.niveau === parseInt(filtreNiveau));
    return filtered;
  };

  const etudiantsFiltres = getFilteredEtudiants();
  const totalEtudiants = etudiantsFiltres.length;
  const etudiantsActifs = etudiantsFiltres.filter(e => e.actif).length;
  const nouveaux = etudiantsFiltres.filter(e => e.nouvelleInscription).length;

  const analyseParTypeFormation = () => {
    const types = {
      'FI': { label: 'Formation Initiale', total: 0 },
      'TA': { label: 'Temps Alterné', total: 0 },
      'Executive': { label: 'Executive', total: 0 }
    };
    etudiantsFiltres.forEach(e => {
      const n = (e.niveauFormation || '').toLowerCase();
      const c = (e.cycle || '').toLowerCase();
      if (n.includes('executive') || c.includes('executive')) types.Executive.total++;
      else if (n.includes('ta') || n.includes('alterne') || c.includes('ta') || c.includes('alterne')) types.TA.total++;
      else types.FI.total++;
    });
    return Object.entries(types).map(([key, d]) => ({ type: key, label: d.label, total: d.total }));
  };

  const analyseParNiveau = () => {
    const stats = {};
    etudiantsFiltres.forEach(e => {
      const n = e.niveau || 'N/A';
      stats[n] = (stats[n] || 0) + 1;
    });
    return Object.entries(stats)
      .map(([n, total]) => ({ niveau: n, total }))
      .sort((a, b) => (parseInt(a.niveau) || 0) - (parseInt(b.niveau) || 0));
  };



  const analyseParAnneeScolaire = () => {
    const stats = {};
    etudiantsFiltres.forEach(e => {
      const a = e.anneeScolaire || 'N/A';
      stats[a] = (stats[a] || 0) + 1;
    });
    return Object.entries(stats).map(([a, total]) => ({ annee: a, total })).sort((a, b) => b.annee.localeCompare(a.annee));
  };

  const analyseParFiliere = () => {
    const stats = {};
    etudiantsFiltres.forEach(e => {
      const f = e.filiere || 'N/A';
      stats[f] = (stats[f] || 0) + 1;
    });
    return Object.entries(stats).map(([f, total]) => ({ filiere: f, total })).sort((a, b) => b.total - a.total);
  };

  const formationTypes = analyseParTypeFormation();
  const niveaux = analyseParNiveau();
  const annees = analyseParAnneeScolaire();
  const filieres = analyseParFiliere();

  const anneesDisponibles = [...new Set(etudiants.map(e => e.anneeScolaire).filter(Boolean))].sort((a, b) => b.localeCompare(a));
  const niveauxDisponibles = [...new Set(etudiants.map(e => e.niveau).filter(n => n !== undefined))].sort((a, b) => a - b);
  const resetFilters = () => {
    setSearchTerm('');
    setFiltreAnneeScolaire(anneesDisponibles[0] || 'toutes');
    setFiltreNiveau('tous');
  };

  const isFiltered = filtreAnneeScolaire !== 'toutes' || filtreNiveau !== 'tous' || searchTerm;



  if (loading) return (
    <div className="state-screen">
      <div className="spinner"></div>
      <p>Chargement...</p>
    </div>
  );

  if (error) return (
    <div className="state-screen">
      <p className="err-msg">{error}</p>
      <button onClick={fetchData} className="btn-retry"><RefreshCw size={13} /> Réessayer</button>
    </div>
  );

  return (
    <div className="layout">
      <Sidebar onLogout={handleLogout} />

      <div className="main">
        <button className="toggle-btn" onClick={() => setHeaderVisible(!headerVisible)}>
          {headerVisible ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {/* ── Header ── */}
        {headerVisible && (
          <div className="page-header">
            <div className="header-row">
              <div>
                <p className="header-eyebrow">Dashboard pédagogique</p>
                <h1 className="header-title">
                  {userInfo?.filiere === 'GENERAL' ? 'GÉNÉRAL' : userInfo?.filiere || stats?.filiere || '—'}
                </h1>
              </div>
              <div className="header-pills">
                <span className="pill">{totalEtudiants} étudiants</span>
                <span className="pill">{cours.length} cours</span>
                <span className="pill">{professeurs.length} professeurs</span>
              </div>
            </div>

            <div className="filters-row">
              <div className="search-wrap">
                <Search size={13} className="s-icon" />
                <input
                  className="s-input"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
                {searchTerm && <button className="s-clear" onClick={() => setSearchTerm('')}><X size={11} /></button>}
              </div>
              <select className="f-select" value={filtreAnneeScolaire} onChange={e => setFiltreAnneeScolaire(e.target.value)}>
                <option value="toutes">Toutes les années</option>
                {anneesDisponibles.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <select className="f-select" value={filtreNiveau} onChange={e => setFiltreNiveau(e.target.value)}>
                <option value="tous">Tous les niveaux</option>
                {niveauxDisponibles.map(n => <option key={n} value={n}>Niveau {n}</option>)}
              </select>
              <button className="f-reset" onClick={resetFilters}><RefreshCw size={12} /> Reset</button>
            </div>

            {isFiltered && <p className="filter-note">{totalEtudiants} résultats — filtres actifs</p>}
          </div>
        )}

        {/* ── Content ── */}
        <div className="content">

          {/* KPIs */}
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-icon" style={{ background: '#dbeafe', color: '#1d4ed8' }}><Users size={18} /></div>
              <div>
                <p className="kpi-label">Total étudiants</p>
                <p className="kpi-val">{totalEtudiants}</p>
                <p className="kpi-sub">{etudiantsActifs} actifs</p>
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-icon" style={{ background: '#dcfce7', color: '#15803d' }}><BookOpen size={18} /></div>
              <div>
                <p className="kpi-label">Cours</p>
                <p className="kpi-val">{cours.length}</p>
                <p className="kpi-sub">disponibles</p>
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-icon" style={{ background: '#fef9c3', color: '#a16207' }}><UserCheck size={18} /></div>
              <div>
                <p className="kpi-label">Professeurs</p>
                <p className="kpi-val">{professeurs.length}</p>
                <p className="kpi-sub">équipe pédagogique</p>
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-icon" style={{ background: '#ede9fe', color: '#6d28d9' }}><Award size={18} /></div>
              <div>
                <p className="kpi-label">Nouvelles inscriptions</p>
                <p className="kpi-val">{nouveaux}</p>
                <p className="kpi-sub">cette année</p>
              </div>
            </div>
          </div>

          {/* Formation types */}
          <div className="section-block">
            <p className="section-label">Types de formation</p>
            <div className="formation-row">
              {formationTypes.map((f, i) => (
                <div key={i} className="fcard">
                  <p className="fcard-code">{f.type}</p>
                  <p className="fcard-val">{f.total}</p>
                  <p className="fcard-name">{f.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Tables row */}
          <div className="tables-row">

            {/* Par niveau */}
            <div className="tcard">
              <p className="tcard-title">Par niveau</p>
              <table className="tbl">
                <thead><tr><th>Niveau</th><th>Étudiants</th></tr></thead>
                <tbody>
                  {niveaux.length > 0 ? niveaux.map((n, i) => (
                    <tr key={i}>
                      <td>Niveau {n.niveau}</td>
                      <td><span className="b b-blue">{n.total}</span></td>
                    </tr>
                  )) : <tr><td colSpan={2} className="no-data">Aucune donnée</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Par année */}
            <div className="tcard">
              <p className="tcard-title">Par année scolaire</p>
              <table className="tbl">
                <thead><tr><th>Année</th><th>Étudiants</th></tr></thead>
                <tbody>
                  {annees.length > 0 ? annees.map((a, i) => (
                    <tr key={i}>
                      <td>{a.annee}</td>
                      <td><span className="b b-blue">{a.total}</span></td>
                    </tr>
                  )) : <tr><td colSpan={2} className="no-data">Aucune donnée</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Par filière — vue générale uniquement */}
            {userInfo?.filiere === 'GENERAL' && (
              <div className="tcard">
                <p className="tcard-title">Par filière</p>
                <table className="tbl">
                  <thead><tr><th>Filière</th><th>Étudiants</th></tr></thead>
                  <tbody>
                    {filieres.length > 0 ? filieres.map((f, i) => (
                      <tr key={i}>
                        <td>{f.filiere}</td>
                        <td><span className="b b-blue">{f.total}</span></td>
                      </tr>
                    )) : <tr><td colSpan={2} className="no-data">Aucune donnée</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>

      <style jsx>{`
        /* ── Base ── */
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .layout {
          display: flex;
          min-height: 100vh;
          background: #eff6ff;
          font-family: 'Segoe UI', system-ui, sans-serif;
          font-size: 14px;
          color: #111827;
        }
        .main { flex: 1; min-width: 0; position: relative; }
        .content { max-width: 1100px; margin: 0 auto; padding: 1.5rem; }

        /* ── Toggle ── */
        .toggle-btn {
          position: fixed; top: 1rem; right: 1rem; z-index: 100;
          width: 28px; height: 28px; border-radius: 50%;
          border: 1px solid #bfdbfe; background: #fff;
          color: #3b82f6; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
        }

        /* ── Header ── */
        .page-header {
          background: #fff;
          border-bottom: 1px solid #bfdbfe;
          padding: 1.25rem 1.5rem;
        }
        .header-row {
          display: flex; align-items: flex-start;
          justify-content: space-between; flex-wrap: wrap;
          gap: 1rem; margin-bottom: 1rem;
        }
        .header-eyebrow { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; }
        .header-title { font-size: 20px; font-weight: 600; color: #1e3a8a; }
        .header-pills { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
        .pill {
          font-size: 12px; font-weight: 500;
          padding: 3px 10px; border-radius: 20px;
          background: #dbeafe; color: #1d4ed8;
          border: 1px solid #bfdbfe;
        }

        /* ── Filters ── */
        .filters-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
        .search-wrap { position: relative; display: flex; align-items: center; }
        .s-icon { position: absolute; left: 8px; color: #9ca3af; pointer-events: none; }
        .s-input {
          padding: 6px 24px 6px 26px;
          border: 1px solid #bfdbfe; border-radius: 6px;
          font-size: 13px; background: #f8fafc; color: #111;
          width: 190px; outline: none;
        }
        .s-input:focus { border-color: #3b82f6; background: #fff; }
        .s-clear { position: absolute; right: 6px; background: none; border: none; color: #9ca3af; cursor: pointer; display: flex; }
        .f-select {
          padding: 6px 10px; border: 1px solid #bfdbfe;
          border-radius: 6px; font-size: 13px;
          background: #f8fafc; color: #111; outline: none; cursor: pointer;
        }
        .f-select:focus { border-color: #3b82f6; }
        .f-reset {
          padding: 6px 12px; border: 1px solid #bfdbfe;
          border-radius: 6px; font-size: 13px;
          background: #fff; color: #3b82f6; cursor: pointer;
          display: flex; align-items: center; gap: 5px;
        }
        .f-reset:hover { background: #eff6ff; }
        .filter-note { margin-top: 8px; font-size: 12px; color: #3b82f6; }

        /* ── Section label ── */
        .section-block { margin-top: 1.5rem; }
        .section-label {
          font-size: 11px; text-transform: uppercase;
          letter-spacing: 0.5px; color: #6b7280;
          font-weight: 500; margin-bottom: 0.75rem;
        }

        /* ── KPIs ── */
        .kpi-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 10px;
          margin-top: 1.5rem;
        }
        .kpi {
          background: #fff; border: 1px solid #bfdbfe;
          border-radius: 10px; padding: 1rem 1.25rem;
          display: flex; align-items: flex-start; gap: 12px;
        }
        .kpi-icon {
          width: 36px; height: 36px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .kpi-label { font-size: 12px; color: #6b7280; margin-bottom: 2px; }
        .kpi-val { font-size: 26px; font-weight: 600; color: #1e3a8a; line-height: 1.1; }
        .kpi-sub { font-size: 11px; color: #9ca3af; }

        /* ── Formation cards ── */
        .formation-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 10px;
        }
        .fcard {
          background: #fff; border: 1px solid #bfdbfe;
          border-radius: 10px; padding: 1rem 1.25rem;
        }
        .fcard-code { font-size: 11px; font-weight: 600; color: #3b82f6; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
        .fcard-val { font-size: 28px; font-weight: 600; color: #1e3a8a; line-height: 1; margin-bottom: 4px; }
        .fcard-name { font-size: 12px; color: #6b7280; }

        /* ── Tables ── */
        .tables-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 10px;
          margin-top: 1.5rem;
        }
        .tcard {
          background: #fff; border: 1px solid #bfdbfe;
          border-radius: 10px; padding: 1rem 1.25rem;
        }
        .tcard-title {
          font-size: 13px; font-weight: 600; color: #1e3a8a;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #dbeafe;
        }
        .table-scroll { overflow-x: auto; max-height: 320px; overflow-y: auto; }
        .tbl { width: 100%; border-collapse: collapse; font-size: 13px; }
        .tbl thead th {
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px;
          color: #9ca3af; font-weight: 500; padding: 0 0 8px;
          text-align: left; border-bottom: 1px solid #dbeafe;
        }
        .tbl tbody td {
          padding: 7px 0; color: #374151;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
        }
        .tbl tbody tr:last-child td { border-bottom: none; }
        .tbl tbody tr:hover { background: #f0f7ff; }
        .no-data { text-align: center; color: #bbb; padding: 1rem 0; font-size: 13px; }

        /* ── Badges ── */
        .b {
          display: inline-block; font-size: 11px; font-weight: 500;
          padding: 2px 8px; border-radius: 20px;
        }
        .b-blue   { background: #dbeafe; color: #1d4ed8; }
        .b-green  { background: #dcfce7; color: #15803d; }
        .b-amber  { background: #fef9c3; color: #a16207; }
        .b-purple { background: #ede9fe; color: #6d28d9; }
        .b-red    { background: #fee2e2; color: #b91c1c; }
        .b-gray   { background: #f3f4f6; color: #4b5563; }

        /* ── States ── */
        .state-screen {
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          min-height: 100vh; gap: 1rem;
          background: #eff6ff; text-align: center;
        }
        .err-msg { color: #b91c1c; font-size: 14px; }
        .spinner {
          width: 32px; height: 32px;
          border: 3px solid #bfdbfe;
          border-left-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .btn-retry {
          display: flex; align-items: center; gap: 6px;
          background: #3b82f6; color: #fff; border: none;
          padding: 7px 14px; border-radius: 6px;
          font-size: 13px; cursor: pointer;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .content { padding: 1rem; }
          .header-row { flex-direction: column; }
          .tables-row { grid-template-columns: 1fr; }
          .kpi-row { grid-template-columns: repeat(2, 1fr); }
          .filters-row { flex-direction: column; align-items: stretch; }
          .s-input { width: 100%; }
        }
        @media (max-width: 480px) {
          .kpi-row, .formation-row { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default PedagogiqueDashboard;